import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  BarChart3, TrendingUp, TrendingDown, Lightbulb, Instagram,
  Users, Image as ImageIcon, Heart, MessageCircle, Loader2, Plug, ExternalLink, Sparkles, Clock, Eye, Film,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Performance({ accountKey: propKey }) {
  const outlet = useOutletContext();
  const accountKey = propKey || outlet?.accountKey;
  const [learnings, setLearnings] = useState([]);
  const [dash, setDash] = useState(null);
  const [meta, setMeta] = useState(null);
  const [insights, setInsights] = useState(null);
  const [recos, setRecos] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [learnStatus, setLearnStatus] = useState(null);
  const [learning, setLearning] = useState(false);

  const loadLearnings = () => api.get("/learnings", { params: { account: accountKey } }).then(({ data }) => setLearnings(data));

  useEffect(() => {
    if (!accountKey) return;
    loadLearnings();
    api.get("/dashboard", { params: { account: accountKey } }).then(({ data }) => setDash(data));
    api.get("/ai/learn/status", { params: { account: accountKey } }).then(({ data }) => setLearnStatus(data)).catch(() => {});
  }, [accountKey]);

  const runLearn = async () => {
    setLearning(true);
    try {
      const { data } = await api.post("/ai/learn", { account_key: accountKey });
      if (data.skipped) {
        toast.error(data.skipped);
      } else {
        await loadLearnings();
        const { data: st } = await api.get("/ai/learn/status", { params: { account: accountKey } });
        setLearnStatus(st);
        toast.success(`Lernzyklus fertig · ${data.learnings_created} Learnings aus ${data.sample} Beiträgen`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lernzyklus fehlgeschlagen");
    } finally {
      setLearning(false);
    }
  };

  useEffect(() => {
    if (!accountKey) return;
    api.get("/learnings", { params: { account: accountKey } }).then(({ data }) => setLearnings(data));
    api.get("/dashboard", { params: { account: accountKey } }).then(({ data }) => setDash(data));
  }, [accountKey]);

  useEffect(() => {
    if (!accountKey) return;
    let cancelled = false;
    setLoadingMeta(true);
    setInsights(null);
    setRecos(null);
    api.get("/meta/status", { params: { account: accountKey } })
      .then(({ data }) => {
        if (cancelled) return;
        setMeta(data);
        if (data.connected) {
          api.get("/meta/recommendations", { params: { account: accountKey } })
            .then(({ data: rec }) => { if (!cancelled) setRecos(rec); })
            .catch(() => {});
          return api.get("/meta/insights", { params: { account: accountKey } })
            .then(({ data: ins }) => { if (!cancelled) setInsights(ins); })
            .catch(() => { if (!cancelled) setInsights({ available: false, reason: "Insights derzeit nicht verfügbar." }); });
        }
      })
      .catch(() => { if (!cancelled) setMeta({ connected: false }); })
      .finally(() => { if (!cancelled) setLoadingMeta(false); });
    return () => { cancelled = true; };
  }, [accountKey]);

  return (
    <div className="p-6 md:p-8 max-w-[1100px] mx-auto animate-rise">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
        <BarChart3 className="h-3.5 w-3.5" /> Performance-Learnings
      </p>
      <h1 className="font-head text-3xl font-bold text-neutral-900 tracking-tight mt-1">Was funktioniert — und warum</h1>

      {/* Live Instagram insights */}
      <LiveInsights loading={loadingMeta} meta={meta} insights={insights} />
      {meta?.connected && <Recommendations recos={recos} />}

      {/* Dark premium performance snapshot */}
      {dash && (
        <div className="dark-panel mt-6 bg-neutral-900 text-white rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6 border border-neutral-800">
          <Snap label="Veröffentlicht" value={dash.performance.published_count} />
          <Snap label="Ø Reichweite" value={dash.performance.avg_reach.toLocaleString("de-DE")} />
          <Snap label="Bestes Format" value={dash.performance.best_format} />
          <Snap label="Heiße Themen" value={dash.kpis.hot_topics} />
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-gradient-to-r from-neutral-900 to-neutral-800 text-white p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-300">
            <Sparkles className="h-3.5 w-3.5" /> Selbstlernendes Board
          </div>
          <p className="text-sm text-neutral-200 mt-1">
            {learnStatus?.last_run
              ? <>Zuletzt gelernt: {new Date(learnStatus.last_run).toLocaleDateString("de-DE")}{learnStatus.best_time ? ` · Beste Zeit: ${learnStatus.best_time}` : ""}</>
              : "Das Board wertet eure echte Posting-History automatisch wöchentlich aus."}
          </p>
        </div>
        <button data-testid="run-learn-btn" onClick={runLearn} disabled={learning}
          className="shrink-0 inline-flex items-center gap-2 bg-white text-neutral-900 text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-neutral-100 transition disabled:opacity-60">
          {learning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {learning ? "Lerne…" : "Jetzt lernen"}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {learnings.map((l) => {
          const up = l.trend === "up";
          return (
            <div key={l.id} data-testid={`learning-${l.id}`} className="bg-white border border-neutral-200 rounded-xl p-5">
              <h3 className="font-head font-semibold text-neutral-900 text-[16px] leading-snug">{l.title}</h3>
              <span className={`mt-2 inline-flex items-start gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full leading-snug ${up ? "bg-green-50 text-green-700" : "bg-rose-50 text-rose-700"}`}>
                {up ? <TrendingUp className="h-3.5 w-3.5 shrink-0 mt-px" /> : <TrendingDown className="h-3.5 w-3.5 shrink-0 mt-px" />}
                <span>{l.metric}</span>
              </span>
              <p className="text-sm text-neutral-500 mt-2 leading-relaxed">{l.insight}</p>
              {l.recommendation && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5">
                  <Lightbulb className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                  <span className="text-[13px] text-blue-900">{l.recommendation}</span>
                </div>
              )}
            </div>
          );
        })}
        {learnings.length === 0 && <div className="col-span-full text-center text-neutral-400 py-16">Noch keine Learnings.</div>}
      </div>
    </div>
  );
}

function fmtLabel(m) {
  const t = m.media_product_type || m.media_type;
  return { REELS: "Reel", VIDEO: "Video", CAROUSEL_ALBUM: "Karussell", IMAGE: "Bild", FEED: "Beitrag" }[t] || "Beitrag";
}

function LiveInsights({ loading, meta, insights }) {
  const [mediaFilter, setMediaFilter] = useState("all");
  const [mediaSort, setMediaSort] = useState("recent");
  if (loading) {
    return (
      <div className="mt-6 bg-white border border-neutral-200 rounded-xl p-6 flex items-center gap-2 text-sm text-neutral-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Instagram-Insights werden geladen…
      </div>
    );
  }
  if (!meta?.connected) {
    return (
      <div data-testid="live-insights-disconnected" className="mt-6 bg-white border border-dashed border-neutral-300 rounded-xl p-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-pink-500 to-blue-600 flex items-center justify-center shrink-0">
            <Instagram className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-head font-semibold text-neutral-900">Live-Insights aktivieren</h3>
            <p className="text-sm text-neutral-500 mt-0.5 max-w-xl">
              Verbinde Instagram & Facebook in den Einstellungen, um echte Reichweite, Follower-Entwicklung
              und Post-Performance zu sehen — die Datenbasis für KI-gestützte Erfolgsprognosen.
            </p>
          </div>
        </div>
        <a href="/einstellungen" data-testid="live-insights-connect-link"
          className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800">
          <Plug className="h-4 w-4" /> Jetzt verbinden
        </a>
      </div>
    );
  }
  if (insights && insights.available === false) {
    return (
      <div data-testid="live-insights-unavailable" className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5 text-[13px] text-amber-900">
        <span className="font-semibold">Verbunden ({meta.ig_username ? `@${meta.ig_username}` : meta.page_name}).</span>{" "}
        Live-Insights noch nicht abrufbar: {insights.reason}
      </div>
    );
  }
  const p = insights?.profile || {};
  const media = insights?.recent_media || [];
  return (
    <div data-testid="live-insights" className="mt-6">
      <div className="dark-panel bg-neutral-900 text-white rounded-2xl p-6 border border-neutral-800">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Instagram className="h-5 w-5 text-pink-400" />
            <span className="font-head font-semibold">Live · @{p.username || insights?.ig_username}</span>
          </div>
          <span className="text-[11px] uppercase tracking-wider text-neutral-400">Echtzeit-Daten von Meta</span>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-6">
          <Snap label="Follower" value={(p.followers_count ?? 0).toLocaleString("de-DE")} icon={Users} />
          <Snap label="Beiträge" value={(p.media_count ?? 0).toLocaleString("de-DE")} icon={ImageIcon} />
          <Snap label="Letzte Posts" value={media.length} icon={BarChart3} />
        </div>
        {insights?.account_30d && insights.account_30d.views != null && (
          <div data-testid="account-30d" className="mt-5 pt-5 border-t border-neutral-800">
            <div className="text-[11px] uppercase tracking-wider text-neutral-400 mb-3 flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" /> Konto-Auswertung · letzte 30 Tage · nur Instagram, ohne Facebook-Crossposting
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
              <Snap label="Views" value={(insights.account_30d.views ?? 0).toLocaleString("de-DE")} />
              <Snap label="Reichweite" value={(insights.account_30d.reach ?? 0).toLocaleString("de-DE")} />
              <Snap label="Profilaufrufe" value={(insights.account_30d.profile_views ?? 0).toLocaleString("de-DE")} />
              <Snap label="Interaktionen" value={(insights.account_30d.total_interactions ?? 0).toLocaleString("de-DE")} />
              <Snap label="Konten erreicht" value={(insights.account_30d.accounts_engaged ?? 0).toLocaleString("de-DE")} />
            </div>
          </div>
        )}
      </div>

      {insights?.video_summary && (insights.video_summary.avg_views != null || insights.video_summary.avg_reach != null) && (
        <div data-testid="video-summary" className="mt-3 bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Film className="h-4 w-4 text-fuchsia-500" />
            <h3 className="font-head font-semibold text-neutral-900 text-[15px]">Video- & Reichweiten-Auswertung</h3>
            <span className="text-[11px] text-neutral-400 ml-auto">aus {insights.video_summary.sample_size} echten Beiträgen{insights.video_summary.reel_count ? ` · ${insights.video_summary.reel_count} Reels` : ""}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Snap label="Ø Views / Beitrag" value={(insights.video_summary.avg_views ?? 0).toLocaleString("de-DE")} icon={Eye} />
            <Snap label="Ø Reichweite" value={(insights.video_summary.avg_reach ?? 0).toLocaleString("de-DE")} icon={BarChart3} />
            <Snap label="Views gesamt" value={(insights.video_summary.total_views ?? 0).toLocaleString("de-DE")} icon={TrendingUp} />
            <Snap label="Ø Reel-Watchtime" value={insights.video_summary.avg_watch_time_ms ? `${(insights.video_summary.avg_watch_time_ms / 1000).toFixed(1)}s` : "—"} icon={Film} />
          </div>
          {insights.video_summary.best_post && (
            <a href={insights.video_summary.best_post.permalink} target="_blank" rel="noreferrer"
              data-testid="video-best-post"
              className="mt-4 flex items-start gap-3 rounded-lg bg-fuchsia-50 border border-fuchsia-100 px-4 py-3 hover:border-fuchsia-300 transition">
              <TrendingUp className="h-4 w-4 text-fuchsia-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-fuchsia-600">Bester Beitrag · {(insights.video_summary.best_post.views ?? 0).toLocaleString("de-DE")} Views</div>
                <div className="text-[13px] text-neutral-700 line-clamp-1 mt-0.5">{insights.video_summary.best_post.caption || "—"}</div>
              </div>
              <ExternalLink className="h-3.5 w-3.5 text-fuchsia-500 ml-auto shrink-0 mt-0.5" />
            </a>
          )}
        </div>
      )}

      {media.length > 0 && (() => {
        const reelCount = media.filter((m) => (m.media_product_type || m.media_type) === "REELS").length;
        const base = mediaFilter === "reels"
          ? media.filter((m) => (m.media_product_type || m.media_type) === "REELS")
          : media;
        const sorted = mediaSort === "views"
          ? [...base].sort((a, b) => (b.insights?.views ?? b.like_count ?? 0) - (a.insights?.views ?? a.like_count ?? 0))
          : base;
        const shown = sorted.slice(0, 9);
        return (
        <div className="mt-4 bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className="font-head font-semibold text-neutral-900 text-[15px]">Letzte Beiträge — echte Performance</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-lg border border-neutral-200 p-0.5 bg-neutral-50">
                <button
                  data-testid="media-sort-recent"
                  onClick={() => setMediaSort("recent")}
                  className={`text-[12px] font-medium px-3 py-1 rounded-md transition ${mediaSort === "recent" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500"}`}
                >
                  Neueste
                </button>
                <button
                  data-testid="media-sort-views"
                  onClick={() => setMediaSort("views")}
                  className={`text-[12px] font-medium px-3 py-1 rounded-md transition inline-flex items-center gap-1 ${mediaSort === "views" ? "bg-white shadow-sm text-fuchsia-700" : "text-neutral-500"}`}
                >
                  <TrendingUp className="h-3 w-3" /> Top-Views
                </button>
              </div>
              <div className="inline-flex rounded-lg border border-neutral-200 p-0.5 bg-neutral-50">
                <button
                  data-testid="media-filter-all"
                  onClick={() => setMediaFilter("all")}
                  className={`text-[12px] font-medium px-3 py-1 rounded-md transition ${mediaFilter === "all" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500"}`}
                >
                  Alle ({media.length})
                </button>
                <button
                  data-testid="media-filter-reels"
                  onClick={() => setMediaFilter("reels")}
                  disabled={reelCount === 0}
                  className={`text-[12px] font-medium px-3 py-1 rounded-md transition inline-flex items-center gap-1 disabled:opacity-40 ${mediaFilter === "reels" ? "bg-white shadow-sm text-fuchsia-700" : "text-neutral-500"}`}
                >
                  <Film className="h-3 w-3" /> Reels ({reelCount})
                </button>
              </div>
            </div>
          </div>
          {shown.length === 0 ? (
            <p className="text-[13px] text-neutral-400 py-6 text-center">Keine Reels in den letzten Beiträgen gefunden.</p>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {shown.map((m) => {
              const isReel = (m.media_product_type || m.media_type) === "REELS";
              return (
              <a key={m.id} href={m.permalink} target="_blank" rel="noreferrer"
                data-testid={`live-media-${m.id}`}
                className="group rounded-lg border border-neutral-200 overflow-hidden hover:border-neutral-400 transition">
                <div className="aspect-video bg-neutral-100 overflow-hidden relative">
                  {(m.thumbnail_url || m.media_url) ? (
                    <img src={m.thumbnail_url || m.media_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                  )}
                  <span className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${isReel ? "bg-fuchsia-600 text-white" : "bg-black/60 text-white"}`}>
                    {isReel && <Film className="h-2.5 w-2.5" />} {fmtLabel(m)}
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-[12px] text-neutral-600 line-clamp-2 leading-snug min-h-[32px]">{m.caption || "—"}</p>
                  <div className="mt-2 flex items-center gap-4 text-[12px] text-neutral-500">
                    <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5 text-rose-500" /> {m.like_count ?? "—"}</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5 text-blue-500" /> {m.comments_count ?? "—"}</span>
                    <ExternalLink className="h-3.5 w-3.5 ml-auto opacity-0 group-hover:opacity-100 transition" />
                  </div>
                  {m.insights && (m.insights.views != null || m.insights.reach != null) && (
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-neutral-400 border-t border-neutral-100 pt-1.5">
                      {m.insights.views != null && <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {m.insights.views.toLocaleString("de-DE")}</span>}
                      {m.insights.reach != null && <span>Reichw. {m.insights.reach.toLocaleString("de-DE")}</span>}
                      {m.insights.saved != null && <span>💾 {m.insights.saved}</span>}
                    </div>
                  )}
                </div>
              </a>
              );
            })}
          </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}

function Recommendations({ recos }) {
  if (!recos || recos.available === false) return null;
  const max = recos.by_format?.[0]?.avg || 1;
  return (
    <div data-testid="reco-panel" className="mt-4 bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-fuchsia-500" />
        <h3 className="font-head font-semibold text-neutral-900 text-[15px]">Bestes Format · Beste Uhrzeit</h3>
        <span className="text-[11px] text-neutral-400 ml-auto">aus {recos.sample_size} echten Beiträgen</span>
      </div>
      <p className="text-[12px] text-neutral-500 mt-0.5 mb-4">KI-Empfehlung auf Basis eurer echten Performance.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg bg-fuchsia-50 border border-fuchsia-100 px-4 py-3" data-testid="reco-best-format">
          <div className="text-[11px] uppercase tracking-wider text-fuchsia-600">Bestes Format</div>
          <div className="text-lg font-bold text-neutral-900 mt-0.5">{recos.best_format || "—"}</div>
        </div>
        <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3" data-testid="reco-best-time">
          <div className="text-[11px] uppercase tracking-wider text-indigo-600 flex items-center gap-1"><Clock className="h-3 w-3" /> Beste Zeit</div>
          <div className="text-lg font-bold text-neutral-900 mt-0.5">{recos.best_day ? `${recos.best_day}, ${recos.best_time_label}` : "—"}</div>
        </div>
      </div>
      {recos.by_format?.length > 0 && (
        <div className="space-y-2">
          {recos.by_format.map((f) => (
            <div key={f.format} className="flex items-center gap-3">
              <span className="text-[12px] text-neutral-600 w-24 shrink-0">{f.format}</span>
              <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-fuchsia-500 to-indigo-500" style={{ width: `${Math.max(6, (f.avg / max) * 100)}%` }} />
              </div>
              <span className="text-[12px] font-semibold text-neutral-700 w-24 text-right">{f.avg} Ø · {f.count}×</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Snap({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="font-head text-2xl font-bold flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-neutral-400 mt-0.5">{label}</div>
    </div>
  );
}
